import {
  colorSuccess200,
  colorSuccess500,
  colorSuccess600,
} from '@amzn/meridian-tokens/base/color';
import {
  DataKind,
  GetShiftPlanOutputResponse,
  ProjectedVolumeDataPoint,
  ShiftSummaryCategory,
} from '../pages/reviewAndPublish/model';

/**
 * Mid-shift re-plan: actual vs projected display helpers (UTR-REFLEX-1612, UTR-REFLEX-1613).
 *
 * On a mid-shift re-plan the plan output starts with the hours already worked (dataKind ACTUAL,
 * observed by FCLM) followed by the hours the engine planned (dataKind PROJECTED). Everything here
 * treats a missing or null dataKind as PROJECTED, so Start-of-Shift plans and plans written before the
 * field existed render exactly as before.
 *
 * The UI shows actuals only when RCC_MID_SHIFT_REPLAN is enabled for the user or the site. With the
 * flag off (or still resolving) the ACTUAL buckets are hidden entirely.
 */

/** Feature flag gating the actual-vs-projected display. Same literal as Commons FeatureFlags.cfg. */
export const MID_SHIFT_REPLAN_FEATURE_NAME = 'RCC_MID_SHIFT_REPLAN';

/** Legend/series name of the actuals line on the volume chart. */
export const ACTUALLY_PROCESSED_LABEL = 'Actually processed';

/**
 * Green used for actuals (Meridian success palette). The line uses the mid tone; header text uses the
 * darker tone so it stays readable on a white surface. Dark mode uses the light tone for both.
 */
export const getActualColors = (isDark: boolean): { line: string; text: string } => ({
  line: isDark ? colorSuccess200 : colorSuccess500,
  text: isDark ? colorSuccess200 : colorSuccess600,
});

/** True only for buckets explicitly tagged ACTUAL; absent/null/PROJECTED are projected. */
export const isActual = (item?: { dataKind?: DataKind | null } | null): boolean =>
  item?.dataKind === DataKind.ACTUAL;

/**
 * The primary grading volume series of a plan output. Reads primaryGradingVolumeTrend and falls back
 * to the deprecated projectedPrimaryGradingVolume (same series) for responses from a backend that
 * doesn't send the new field yet. Every reader of the volume curve goes through here.
 */
export const getVolumeTrend = (
  output?: {
    primaryGradingVolumeTrend?: ProjectedVolumeDataPoint[] | null;
    projectedPrimaryGradingVolume?: ProjectedVolumeDataPoint[] | null;
  } | null
): ProjectedVolumeDataPoint[] | undefined =>
  output?.primaryGradingVolumeTrend ?? output?.projectedPrimaryGradingVolume ?? undefined;

/** The volume series without its ACTUAL points (what the chart draws when actuals are hidden). */
export const withoutActualPoints = (
  series?: ProjectedVolumeDataPoint[] | null
): ProjectedVolumeDataPoint[] => {
  const points = series ?? [];
  // Same reference when nothing is actual (every Start-of-Shift plan), so memoised consumers
  // don't see a "new" series.
  return points.some(isActual) ? points.filter(point => !isActual(point)) : points;
};

export interface SplitVolumeSeries {
  /** The ACTUAL points, in order (the "Actually processed" line). */
  actual: ProjectedVolumeDataPoint[];
  /**
   * The projected points, in order. When there are actuals, the last actual point is repeated at the
   * start so the projected line begins where the actual line ends instead of leaving a gap.
   */
  projected: ProjectedVolumeDataPoint[];
}

/** Splits a cumulative volume series at the mid-shift cut-over. */
export const splitVolumeSeries = (
  series?: ProjectedVolumeDataPoint[] | null
): SplitVolumeSeries => {
  const actual = (series ?? []).filter(isActual);
  const projected = withoutActualPoints(series);
  const lastActual = actual[actual.length - 1];
  if (lastActual && projected.length > 0) {
    return { actual, projected: [{ ...lastActual, dataKind: DataKind.PROJECTED }, ...projected] };
  }
  return { actual, projected };
};

/** Shift summary with the ACTUAL time buckets removed from every category. */
export const withoutActualBuckets = (
  shiftSummary?: ShiftSummaryCategory[] | null
): ShiftSummaryCategory[] | undefined => {
  if (!shiftSummary) {
    return undefined;
  }
  // Same reference when nothing is actual (every Start-of-Shift plan).
  const hasActual = shiftSummary.some(category => (category.planOutput ?? []).some(isActual));
  if (!hasActual) {
    return shiftSummary;
  }
  return shiftSummary.map(category => ({
    ...category,
    planOutput: (category.planOutput ?? []).filter(bucket => !isActual(bucket)),
  }));
};

/**
 * Applies the RCC_MID_SHIFT_REPLAN gate to a plan output: unchanged when actuals are shown, otherwise
 * the ACTUAL volume points and time buckets are removed so the plan reads as today (projected only).
 */
export const applyActualsGate = (
  output: GetShiftPlanOutputResponse | undefined,
  showActuals: boolean
): GetShiftPlanOutputResponse | undefined => {
  if (!output || showActuals) {
    return output;
  }
  // Both volume fields are gated: readers use primaryGradingVolumeTrend, falling back to the
  // deprecated field, so neither may leak actual points.
  const trend = output.primaryGradingVolumeTrend;
  const projectedTrend = trend ? withoutActualPoints(trend) : trend;
  const volume = output.projectedPrimaryGradingVolume;
  const projectedVolume = volume ? withoutActualPoints(volume) : volume;
  const projectedSummary = withoutActualBuckets(output.shiftSummary) ?? output.shiftSummary;
  if (projectedTrend === trend && projectedVolume === volume && projectedSummary === output.shiftSummary) {
    return output;
  }
  return {
    ...output,
    ...(trend !== undefined && { primaryGradingVolumeTrend: projectedTrend }),
    projectedPrimaryGradingVolume: projectedVolume,
    shiftSummary: projectedSummary,
  };
};

/**
 * Ordered union of several time-label lists, for the chart's x-axis.
 *
 * Bare "HH:mm" labels can't be sorted directly across midnight ("00:00" would land before "23:00"),
 * and the series can have gaps (e.g. a missing 00:00 point), so neighbouring labels aren't a reliable
 * anchor either. A shift spans well under 24h, so on the 24h clock its times leave one large empty
 * stretch: the shift starts at the first time after the largest gap between consecutive times. Times
 * are then sorted by minutes elapsed since that start.
 */
export const mergeTimesInOrder = (lists: string[][]): string[] => {
  // concat rather than .flat(): the build's lib target predates ES2019.
  const unique = Array.from(new Set(([] as string[]).concat(...lists)));
  const parsed = unique.map(time => ({ time, minutes: toMinutes(time) }));
  // Labels that aren't "HH:mm" can't be placed; keep them in input order at the end.
  const timed = parsed.filter(p => p.minutes !== null) as { time: string; minutes: number }[];
  const untimed = parsed.filter(p => p.minutes === null).map(p => p.time);
  if (timed.length === 0) {
    return untimed;
  }

  const byClock = [...timed].sort((a, b) => a.minutes - b.minutes);
  // Largest circular gap: from each time to the next (the last one wraps to the first + 24h).
  let shiftStart = byClock[0].minutes;
  let largestGap = -1;
  byClock.forEach((point, i) => {
    const next = byClock[(i + 1) % byClock.length];
    const gap =
      (next.minutes - point.minutes + MINUTES_PER_DAY) % MINUTES_PER_DAY || MINUTES_PER_DAY;
    if (gap > largestGap) {
      largestGap = gap;
      shiftStart = next.minutes;
    }
  });

  const sinceStart = (minutes: number) =>
    (minutes - shiftStart + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return [
    ...timed.sort((a, b) => sinceStart(a.minutes) - sinceStart(b.minutes)).map(p => p.time),
    ...untimed,
  ];
};

const MINUTES_PER_DAY = 24 * 60;

/** "HH:mm" (or "H:mm") → minutes after midnight; null if not a valid clock time. */
const toMinutes = (time: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
};
